import { SEO } from '../components/SEO';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy - SpinEmerge',
  description: 'Privacy policy for SpinEmerge.com'
};

export const Privacy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy - SpinEmerge"
        description="Read our privacy policy to understand how SpinEmerge.com collects, uses, and protects your personal information."
        canonical="/privacy"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen pt-20 pb-20">
        <section className="py-12 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-8 text-white">Privacy Policy</h1>
            <p className="text-gray-400 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

            <div className="space-y-8 text-gray-300">
              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
                <p className="leading-relaxed">
                  At SpinEmerge.com, we are committed to protecting your privacy. This Privacy Policy explains how we 
                  collect, use, disclose, and safeguard your information when you visit our website.
                </p>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2.1 Automatically Collected Information</h3>
                    <p className="leading-relaxed">
                      When you visit our website, we automatically collect certain information about your device, including 
                      your IP address, browser type, operating system, referring URLs, and pages viewed.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2.2 Cookies and Tracking Technologies</h3>
                    <p className="leading-relaxed">
                      We use cookies and similar tracking technologies to track activity on our website and store certain 
                      information. You can instruct your browser to refuse all cookies or to indicate when a cookie is 
                      being sent.
                    </p>
                  </div>
                </div>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
                <p className="leading-relaxed mb-4">We use the information we collect to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide, maintain, and improve our website</li>
                  <li>Analyze how users interact with our website</li>
                  <li>Personalize your experience</li>
                  <li>Send you marketing communications (with your consent)</li>
                  <li>Detect and prevent fraud or abuse</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">4. Information Sharing and Disclosure</h2>
                <p className="leading-relaxed mb-4">
                  We do not sell your personal information. We may share your information in the following circumstances:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>With service providers who assist us in operating our website</li>
                  <li>When required by law or to protect our rights</li>
                  <li>In connection with a business transfer or merger</li>
                  <li>With your consent or at your direction</li>
                </ul>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
                <p className="leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal information. 
                  However, no method of transmission over the Internet or electronic storage is 100% secure, and we 
                  cannot guarantee absolute security.
                </p>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">6. Your Rights</h2>
                <p className="leading-relaxed mb-4">Under GDPR and UK data protection laws, you have the right to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Access your personal information</li>
                  <li>Rectify inaccurate information</li>
                  <li>Request deletion of your information</li>
                  <li>Object to processing of your information</li>
                  <li>Request restriction of processing</li>
                  <li>Data portability</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">7. Third-Party Links</h2>
                <p className="leading-relaxed">
                  Our website contains links to third-party websites, including casino websites. We are not responsible 
                  for the privacy practices of these external sites. We encourage you to review the privacy policies 
                  of any third-party sites you visit.
                </p>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">8. Children's Privacy</h2>
                <p className="leading-relaxed">
                  Our website is not intended for individuals under the age of 18. We do not knowingly collect personal 
                  information from children. If you believe we have collected information from a child, please contact 
                  us immediately.
                </p>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">9. Changes to This Privacy Policy</h2>
                <p className="leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the 
                  new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">10. Contact Us</h2>
                <p className="leading-relaxed">
                  If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us 
                  at info@spinemerge.com.
                </p>
              </section>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

